import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';

type PeekData = {
  status: 'valid' | 'used' | 'expired' | 'invalid';
  email?: string;
  instance_key?: string;
  instance_name?: string;
  purpose?: 'login' | 'save';
};

type PageState =
  | { status: 'loading' }
  | { status: 'ready'; data: PeekData }
  | { status: 'consume-error'; message: string; purpose?: 'login' | 'save' }
  | { status: 'peek-error' };

export default function MagicLinkVerify() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [isConsuming, setIsConsuming] = useState(false);
  const token = searchParams.get('token');

  useEffect(() => {
    const peekToken = async () => {
      if (!token) {
        setState({ status: 'ready', data: { status: 'invalid' } });
        return;
      }

      try {
        const data = await apiService.peekMagicLink(token);
        setState({ status: 'ready', data });
      } catch (error) {
        setState({ status: 'peek-error' });
      }
    };

    peekToken();
  }, [token]);

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleSaveCurrentFirst = () => {
    localStorage.setItem('showExitDialog', 'true');
    window.location.href = '/';
  };

  const handleContinue = async () => {
    if (!token) return;

    setIsConsuming(true);
    try {
      const data = await apiService.consumeMagicLink(token);
      apiService.storeCredentialsFromMagicLink(data);
      window.location.href = '/';
    } catch (error: any) {
      let message = error.response?.data?.message || 'Failed to sign in.';
      if (error.response?.status === 409) {
        message = error.response?.data?.code === 'email_taken'
          ? error.response.data.message
          : 'This link was already used.';
      } else if (error.response?.status === 401) {
        message = 'This link is invalid or expired.';
      } else if (error.response?.status === 410) {
        message = 'This instance no longer exists.';
      }
      setState({ status: 'consume-error', message, purpose: state.status === 'ready' ? state.data.purpose : undefined });
    } finally {
      setIsConsuming(false);
    }
  };

  const renderStatusCard = (title: string, message: string) => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-heading mb-2">{title}</h2>
        <p className="text-body-color">{message}</p>
      </div>
      <div className="flex gap-4">
        <Link to="/signin" className="flex-1 btn-primary-full font-medium text-center">
          Sign in
        </Link>
        <button onClick={handleGoHome} className="flex-1 btn-secondary font-medium">
          Go to Home
        </button>
      </div>
    </div>
  );

  const renderWelcome = (data: PeekData) => {
    const currentInstance = apiService.getInstanceInfo();
    const hasDifferentInstance = currentInstance && currentInstance.instanceKey !== data.instance_key;
    const isSave = data.purpose === 'save';

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-heading mb-2">{isSave ? 'Confirm your email' : 'Welcome'}</h2>
          {isSave ? (
            <div className="space-y-2 text-body-color">
              <p>This link saves {data.instance_name ? `the instance "${data.instance_name}"` : 'your instance'} with {data.email}.</p>
              <p>This signs out every other session of this instance.</p>
            </div>
          ) : (
            <p className="text-body-color">
              This link signs you in as <strong className="text-heading">{data.email}</strong>.
            </p>
          )}
        </div>

        {hasDifferentInstance && currentInstance.instanceMode === 'temp' && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <h3 className="text-yellow-400 font-medium mb-1 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5" />
              Unsaved instance in this browser
            </h3>
            <p className="text-sm text-body-color">
              If you continue, you lose access to it.
            </p>
          </div>
        )}

        {hasDifferentInstance && currentInstance.instanceMode === 'persistent' && (
          <p className="text-body-color">
            This browser is signed in to another saved instance. Continue to switch.
          </p>
        )}

        <div className="flex gap-4">
          {hasDifferentInstance && (
            <button
              onClick={currentInstance.instanceMode === 'temp' ? handleSaveCurrentFirst : handleGoHome}
              className="flex-1 btn-secondary font-medium"
            >
              {currentInstance.instanceMode === 'temp' ? 'Save current first' : 'Keep current'}
            </button>
          )}
          <button
            onClick={handleContinue}
            disabled={isConsuming}
            className="flex-1 btn-primary-full font-medium flex items-center justify-center gap-2"
          >
            {isConsuming && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
            {isSave
              ? (hasDifferentInstance && currentInstance.instanceMode === 'temp' ? 'Confirm anyway' : 'Confirm')
              : (hasDifferentInstance && currentInstance.instanceMode === 'temp' ? 'Continue anyway' : 'Continue')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-body-bg p-4">
      <div className="bg-widget-bg rounded-lg p-8 max-w-md w-full mx-4 border border-secondary shadow-xl">
        {state.status === 'loading' && (
          <div className="flex flex-col items-center gap-4">
            <ArrowPathIcon className="w-8 h-8 text-primary animate-spin" />
            <p className="text-lg text-heading">Checking your link...</p>
          </div>
        )}

        {state.status === 'consume-error' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-heading mb-2">{state.purpose === 'save' ? 'Confirmation failed' : 'Sign-in failed'}</h2>
              <p className="text-body-color">{state.message}</p>
            </div>
            <button onClick={handleGoHome} className="btn-secondary">
              Go to Home
            </button>
          </div>
        )}

        {state.status === 'peek-error' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-heading mb-2">Link check failed</h2>
              <p className="text-body-color">A network or server error stopped the check. Try again.</p>
            </div>
            <button onClick={() => window.location.reload()} className="btn-primary-full font-medium">
              Try again
            </button>
          </div>
        )}

        {state.status === 'ready' && state.data.status === 'valid' && renderWelcome(state.data)}
        {state.status === 'ready' && state.data.status === 'used' && renderStatusCard(
          'Link already used',
          'This link works once. Request a new link on the Sign in page.',
        )}
        {state.status === 'ready' && state.data.status === 'expired' && renderStatusCard(
          'Link expired',
          'This link expired. Request a new one.',
        )}
        {state.status === 'ready' && state.data.status === 'invalid' && renderStatusCard(
          'Invalid link',
          'This link is invalid.',
        )}
      </div>
    </div>
  );
}
